import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import styled from "styled-components";

const CustomPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    color: "#000000",
    borderColor: "#fff",
    backgroundColor: "#fff",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    background: "linear-gradient(90deg,hsl(248, 77.20%, 70.80%), #244C77)",
    color: "#FFFFFF",
  },
  "& .MuiPaginationItem-root:hover": {
    background: "linear-gradient(90deg,rgb(73, 63, 143),rgb(48, 33, 150))",
    color: "#FFFFFF",
  },
}));

const getButtonStyle = (status) => {
  switch (status) {
    case "Received":
      return { ...styles.button, ...styles.receivedButton };
    case "In-Progress":
      return { ...styles.button, ...styles.inProgressButton };
    case "Accept":
      return { ...styles.button, ...styles.acceptButton };
    case "Reject":
      return { ...styles.button, ...styles.rejectButton };
    default:
      return styles.button;
  }
};

const getButtonIcon = (status) => {
  switch (status) {
    // case "Received":
    //   return <HourglassTopIcon style={{ marginRight: "5px" }} />;
    // case "In-Progress":
    //   return <HourglassTopIcon style={{ marginRight: "5px" }} />;
    case "Accept":
      return <CheckCircleIcon style={{ marginRight: "5px" }} />;
    case "Reject":
      return <HighlightOffIcon style={{ marginRight: "5px" }} />;
    default:
      return null;
  }
};

const styles = {
  container: {
    padding: "20px",
    backgroundColor: "transparent",
  },
  tableContainer: {
    backgroundColor: "transparent",
    borderRadius: "8px",
    overflow: "hidden",
    minHeight: "500px",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: "0 8px",
    width: "100%",
  },
  tableHeader: {
    backgroundColor: "#8F95B2",
  },
  headerRow: {
    backgroundColor: "#8F95B2",
    borderRadius: "8px 8px 0 0",
  },
  headerText: {
    fontWeight: "bold",
    color: "white",
    padding: "12px",
    borderBottom: "none",
  },
  button: {
    fontWeight: "bold",
    fontSize: "12px",
    textTransform: "capitalize",
    padding: "5px 10px",
  },
  receivedButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(117, 193, 55, 0.34)",
    color: "#75C137",
  },
  inProgressButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(35, 144, 255, 0.34)",
    color: "#2390FF",
  },
  acceptButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(117, 193, 55, 0.34)",
    color: "#75C137",
  },
  rejectButton: {
    borderRadius:"6px",
    width:"100px",
    backgroundColor: "rgba(242, 83, 52, 0.34)",
    color: "#F25334",
  },
  pagination: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "center",
  },
};

const DynamicTable = ({ columns = [], rows = [] }) => {
  const rowsPerPage = 10; // Default number of rows per page
  const [page, setPage] = useState(1);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate the rows to display based on the current page
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedRows = rows?.slice(startIndex, endIndex);

  return (
    <Box style={styles.container}>
      <TableContainer style={styles.tableContainer}>
        <Table style={styles.table}>
          <TableHead>
            <TableRow style={styles.headerRow}>
              {columns?.map((column, index) => (
                <TableCell
                  key={index}
                  style={{
                    ...styles.headerText,
                    borderRadius:
                      index === 0
                        ? "8px 0 0 0"
                        : index === columns.length - 1
                        ? "0 8px 0 0"
                        : "0",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRows?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  position: "relative",
                  overflow: "hidden", // Ensure the border-radius effect is visible
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#DAD8FC",
                  },
                  "&:hover td:first-of-type": {
                    backgroundColor: "#DAD8FC",
                    borderTopLeftRadius: "10px",
                    borderBottomLeftRadius: "10px",
                  },
                  "&:hover td:last-of-type": {
                    backgroundColor: "#DAD8FC",
                    borderTopRightRadius: "10px",
                    borderBottomRightRadius: "10px",
                  },
                }}
              >
                {columns?.map((column, colIndex) => (
                  <TableCell key={colIndex} style={{ border: "none", color:"#8F95B2" }}>
                    {column.field === "status" ? (
                      <Button
                        // variant="contained"
                        style={getButtonStyle(row[column.field])}
                        startIcon={getButtonIcon(row[column.field])}
                      >
                        {row[column.field]}
                      </Button>
                    ) : (
                      row[column.field]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box style={styles.pagination}>
        <CustomPagination
          count={Math.ceil(rows.length / rowsPerPage)}
          page={page}
          onChange={handleChangePage}
          variant="outlined"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default DynamicTable;
